import { max } from "mathjs";
import { dayNumberInData } from "../functions";
import calculateAverageGeneration from "./calculateAverageGeneration";
import type {
  CalcOptions,
  DataIndex,
  NumberMatrix,
  TechnologyDatum,
} from "../algorithm.types";

type CalculateShortTermStorageParams = {
  calcOptions: Pick<
    CalcOptions,
    | "season"
    | "noOfTechs"
    | "noOfTimeSlots"
    | "noOfStorTypes"
    | "dayOfTheSeason"
    | "noOfDaysPerSeason"
  >;
  dataIndex: Pick<DataIndex, "shortTermStorage">;
  technologyData: ReadonlyArray<
    Pick<TechnologyDatum, "storageHours" | "emissions" | "efficiency">
  >;
  sliderValues: Array<number>;
  availData: NumberMatrix;
  genData: NumberMatrix;
  averageGenPerDay: NumberMatrix;
};

export default function calculateShortTermStorage({
  calcOptions,
  dataIndex,
  technologyData,
  sliderValues,
  availData,
  genData,
  averageGenPerDay,
}: CalculateShortTermStorageParams): NumberMatrix {
  const { shortTermStorage } = dataIndex;
  const {
    season,
    noOfTechs,
    noOfTimeSlots,
    noOfStorTypes,
    dayOfTheSeason,
    noOfDaysPerSeason,
  } = calcOptions;

  // VARIABLES FOR BATTERY STORAGE CALCULATIONS
  let storVolReq = 0;
  let storVolNow = 0;
  let storVolFilled = 0;
  let storVolRemaining =
    sliderValues[shortTermStorage] *
    technologyData[shortTermStorage].storageHours;
  const storAvail = Array(noOfTimeSlots).fill(sliderValues[shortTermStorage]);

  const storGen = Array(noOfTimeSlots).fill(0);
  const storfill = Array(noOfTimeSlots).fill(0);

  const unusedCap = Array(noOfTimeSlots).fill(0);
  const extraStorFill = Array(noOfTimeSlots).fill(0);
  const extraStorGen = Array(noOfTimeSlots).fill(0);
  const hrPerTimeSlot = 24 / noOfTimeSlots;

  averageGenPerDay = calculateAverageGeneration({
    calcOptions,
    genData,
    averageGenPerDay,
  });

  for (let ir = noOfTechs - noOfStorTypes; storVolReq === 0 && ir >= 0; ir--) {
    if (
      averageGenPerDay[ir][
        dayNumberInData(season, dayOfTheSeason, noOfDaysPerSeason)
      ] > 0 &&
      technologyData[ir].emissions > 0 &&
      Math.min.apply(null, genData[ir]) === 0
    ) {
      // this tech is polluting, but there's at least one point in day it's not generating so let's try and replace with storage
      for (let j = 0; j < noOfTimeSlots; j++) {
        extraStorGen[j] = Math.min(storAvail[j], genData[ir][j]);
        storVolReq += extraStorGen[j] * hrPerTimeSlot;
      }

      if (storVolReq > storVolRemaining) {
        // Not enough volume left for all of this generation so scale it all down
        for (let j = 0; j < noOfTimeSlots; j++) {
          extraStorGen[j] *= storVolRemaining / storVolReq;
        }
        storVolReq = storVolRemaining;
        storVolRemaining = 0;
      } else {
        storVolRemaining -= storVolReq;
      }

      // Now we're looking for low emission generation to fill the storage
      for (let i = 0; i < ir; i++) {
        for (let j = 0; j < noOfTimeSlots; j++) {
          unusedCap[j] = availData[i][j] - genData[i][j];
        }
        if (
          Math.max.apply(null, unusedCap) > 0 &&
          technologyData[i].emissions <
            technologyData[ir].emissions *
              technologyData[shortTermStorage].efficiency
        ) {
          // We can use this tech to fill the storage
          for (let j = 0; j < noOfTimeSlots; j++) {
            extraStorFill[j] = Math.min(unusedCap[j], storAvail[j]);
            storVolFilled +=
              extraStorFill[j] *
              technologyData[shortTermStorage].efficiency *
              hrPerTimeSlot;
          }

          if (storVolFilled > storVolReq) {
            // Oops overfilled, scale back filling
            for (let j = 0; j < noOfTimeSlots; j++) {
              extraStorFill[j] *= storVolReq / storVolFilled;
            }
            storVolReq = 0;
          } else {
            storVolReq -= storVolFilled;
          }

          for (let j = 0; j < noOfTimeSlots; j++) {
            storAvail[j] -= extraStorFill[j];
            genData[i][j] += extraStorFill[j];
          }
        }
      }

      if (storVolReq > 0) {
        // We couldn't find enough spare low-C capacity to fill this. Scale back storage generation and quit whole storage thing
        for (let j = 0; j < noOfTimeSlots; j++) {
          storVolNow += extraStorGen[j] * hrPerTimeSlot;
        }
        for (let j = 0; j < noOfTimeSlots; j++) {
          extraStorGen[j] *= storVolFilled / storVolNow;
        }
      }

      for (let j = 0; j < noOfTimeSlots; j++) {
        storGen[j] += extraStorGen[j];
        storAvail[j] -= extraStorGen[j];
        storfill[j] += extraStorFill[j];
        genData[ir][j] = max(0, genData[ir][j] - extraStorGen[j]);
      }
    }
  }

  for (let j = 0; j < noOfTimeSlots; j++) {
    genData[shortTermStorage][j] = storGen[j];
  }

  return genData;
}
